import { Edit, FileText, Mail, Printer, Upload } from "lucide-react";
import moment from "moment";
import { TooltipText } from "../ui/tooltip";
import { Trash2 } from "lucide-react";
import { SearchBar } from "../SearchBox/SearchBar";

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "COMPLETED":
      return "bg-green-100 text-green-800 border-green-300";
    case "IN TRANSIT":
      return "bg-blue-100 text-blue-800 border-blue-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

export const getSalesColumns = ({
  selectedOrders,
  salesList,
  handleSelectAll,
  handleSelectOrder,
  onEditOrder,
  setSelectedOrderId,
  setFileUploadVisible,
  setNoteModalVisible,
}) => {
  const actions = (row) => [
    {
      key: "upload",
      icon: Upload,
      title: "Upload",
      className: "bg-green-100 text-green-700 hover:bg-green-200",
      onClick: () => {
        setSelectedOrderId(row);
        setFileUploadVisible(true);
      },
    },
    {
      key: "notes",
      icon: FileText,
      title: "Notes",
     className: "bg-gray-100 text-gray-700 hover:bg-gray-200",
      onClick: () => {
        setSelectedOrderId(row);
        setNoteModalVisible(true);
      },
    },
    {
      key: "email",
      icon: Mail,
      title: "Email",
        className: "bg-purple-100 text-purple-700 hover:bg-purple-200",
      onClick: () => {},
    },
    {
      key: "print",
      icon: Printer,
      title: "Print",
        className: "bg-blue-100 text-blue-700 hover:bg-blue-200",
      onClick: () => {},
    },
    {
      key: "edit",
      icon: Edit,
      title: "Edit",
        className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
      onClick: () => onEditOrder(row?.soId),
    },
  ];

  return [
    {
      key: "select",
      label: () => (
        <input
          type="checkbox"
          onChange={handleSelectAll}
          checked={selectedOrders.length === salesList.length}
          className="rounded border-slate-300 text-[#0072FF]"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedOrders.includes(row.id)}
          onChange={() => handleSelectOrder(row.id)}
          className="rounded border-slate-300 text-[#0072FF]"
        />
      ),
    },

    {
      key: "soNumber",
      label: "Order #",
      render: (row) => (
        <button
          onClick={() => onEditOrder(row?.soId)}
          className="text-sm font-semibold text-[#0072FF] transition-all hover:underline hover:text-[#0162d8]"
        >
          {row?.soNumber || "-"}
        </button>
      ),
    },

    {
      key: "soDate",
      label: "Order Date",
      render: (row) =>
        row?.soDate ? moment(row.soDate).format("DD MMM YYYY") : "-",
    },

    {
      key: "shipByDate",
      label: "Ship By Date",
      render: (row) =>
        row?.shipByDate ? moment(row.shipByDate).format("DD MMM YYYY") : "-",
    },

    {
      key: "customer",
      label: "Customer",
      render: (row) => (
        <TooltipText content={row?.customer}>
          <span className="block max-w-[200px] truncate cursor-pointer">
            {row?.customer || "-"}
          </span>
        </TooltipText>
      ),
    },

    {
      key: "warehouse",
      label: "Warehouse",
      render: (row) => (
        <TooltipText content={row?.warehouse}>
          <span className="block max-w-[200px] truncate cursor-pointer">
            {row?.warehouse || "-"}
          </span>
        </TooltipText>
      ),
    },

    {
      key: "allSku",
      label: "SKU Code",
      render: (row) => (
        <TooltipText content={row?.allSku}>
          <span className="block font-mono max-w-[200px] truncate cursor-pointer">
            {row?.allSku || "-"}
          </span>
        </TooltipText>
      ),
    },

    {
      key: "destination",
      label: "Destination",
      thClass:
        "px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 white uppercase tracking-wider",
      render: (row) => <span className="whitespace-nowrap">{row?.destination || "-"}</span>,
    },

    {
      key: "loadStatus",
      label: "Load Status",
      render: (row) => (
        <span
          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
            row?.loadStatus,
          )}`}
        >
          {row?.loadStatus || "-"}
        </span>
      ),
    },

    {
      key: "actions",
      label: () => <span className="text-center block w-full">Actions</span>,
      render: (row) => (
        <div className="flex items-center justify-center space-x-2">
          {actions(row).map((action) => {
            const Icon = action.icon;

            return (
              <button
                key={action.key}
                onClick={action.onClick}
                title={action.title}
                className={`w-6 h-6 rounded flex items-center justify-center transition-all  ${action.className}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      ),
    },
  ];
};





export const getItemColumns = ({
  items,
  handleItemChange,
  uomTypeList,
  quantityTypeList,
}) => {
  return [
    {
      key: "sno",
      label: "SNO",
      render: (_, index) => (
        <span className="text-center block">{index + 1}</span>
      ),
    },

    {
      key: "sku",
      label: "SKU Code",
      thClass:"px-2 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 min-w-[200px]",
      render: (row, index) => (
        <SearchBar
          searchAPI={"v1/AutoSuggestion/GetSkuSuggestion?Prefix="}
          value={row?.sku}
          selectReacord={(d) => {
            handleItemChange(index, "sku", d?.[0]);
            handleItemChange(index, "skuItemDesc", d?.[1]);
          }}
          inputStyle="!py-1"
          minword={1}
          showResults={0}
        />
      ),
    },

    {
      key: "desc",
      label: "Item Desc",
      render: (row, index) => (
        <input
          value={row.skuItemDesc}
          onChange={(e) =>
            handleItemChange(index, "itemDesc", e.target.value)
          }
          className="w-32 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "uot",
      label: "UOT",
      render: (row, index) => (
        <input
          type="number"
          value={row.uot}
          onChange={(e) => {
            const val = e.target.value;
            handleItemChange(index, "uot", val);
            handleItemChange(
              index,
              "caseCount",
              parseFloat(row.casesPerUot || 0) * parseFloat(val || 0)
            );
            handleItemChange(index, "remainingUnit", val);
          }}
          className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "casesPerUot",
      label: "Cases/UOT",
      render: (row, index) => (
        <input
          type="number"
          value={row.casesPerUot}
          onChange={(e) => {
            const val = e.target.value;
            handleItemChange(index, "casesPerUot", val);
            handleItemChange(
              index,
              "caseCount",
              parseFloat(row.remainingUnit || 0) * parseFloat(val || 0)
            );
            handleItemChange(index, "remainingCases", val);
          }}
          className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "remainingUnit",
      label: "Remaining Unit",
      render: (row) => (
        <input
          readOnly
          value={row.remainingUnit}
          className="w-20 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
        />
      ),
    },

    {
      key: "remainingCases",
      label: "Remaining Cases",
      render: (row) => (
        <input
          readOnly
          value={row.remainingCases}
          className="w-20 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
        />
      ),
    },

    {
      key: "caseCount",
      label: "Case Count",
      render: (row, index) => (
        <div className="flex gap-1">
          <input
            readOnly
            value={
              parseFloat(row.uot || 0) *
              parseFloat(row.casesPerUot || 0)
            }
            className="w-16 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
          />
          <select
            value={row?.uomTypeId}
            onChange={(e) => {
              handleItemChange(index, "uomTypeId", e.target.value);
              handleItemChange(
                index,
                "uomType",
                e.target.options[e.target.selectedIndex].text
              );
            }}
            className="w-24 px-1 py-1 border rounded text-xs dark:bg-slate-800"
          >
            <option>SELECT</option>
            {uomTypeList?.map((uom, i) => (
              <option key={i} value={uom.id}>
                {uom.id}
              </option>
            ))}
          </select>
        </div>
      ),
    },

    {
      key: "packing",
      label: "Packing",
      render: (row, index) => (
        <input
          type="number"
          value={row.packing}
          onChange={(e) =>
            handleItemChange(index, "packing", e.target.value)
          }
          className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "quantity",
      label: "Quantity",
      render: (row, index) => (
        <div className="flex gap-1">
          <input
            type="number"
            value={row.quantity}
            onChange={(e) =>
              handleItemChange(index, "quantity", e.target.value)
            }
            className="w-16 px-2 py-1 border rounded text-xs dark:bg-slate-800"
          />
          <select
            value={row.metricId}
            onChange={(e) => {
              handleItemChange(index, "metricId", e.target.value);
              handleItemChange(
                index,
                "metricType",
                e.target.options[e.target.selectedIndex].text
              );
            }}
            className="w-16 px-1 py-1 border rounded text-xs dark:bg-slate-800"
          >
            <option>SELECT</option>
            {quantityTypeList?.map((qty, i) => (
              <option key={i} value={qty.id}>
                {qty.incoterm}
              </option>
            ))}
          </select>
        </div>
      ),
    },

    {
      key: "price",
      label: "Price",
      render: (row, index) => (
        <input
          type="number"
          value={row.price}
          onChange={(e) =>
            handleItemChange(index, "price", e.target.value)
          }
          className="w-20 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "total",
      label: "Total",
      render: (row) => (
        <input
          readOnly
          value={row.total}
          className="w-20 px-2 py-1 border rounded text-xs bg-slate-100 dark:bg-slate-800"
        />
      ),
    },

    {
      key: "source",
      label: "Source",
      render: (row) => (
        <span className="text-xs text-slate-600 dark:text-slate-400">
          {row.source}
        </span>
      ),
    },

    {
      key: "notes",
      label: "Notes",
      render: (row, index) => (
        <input
          value={row.notes}
          onChange={(e) =>
            handleItemChange(index, "notes", e.target.value)
          }
          placeholder="Notes..."
          className="w-20 px-2 py-1 border rounded text-xs dark:bg-slate-800"
        />
      ),
    },

    {
      key: "delete",
      label: "",
      render: (_, index) => (
        <button
          onClick={() =>
            handleItemChange(index, "isActive", false)
          }
          className="w-6 h-6 rounded flex items-center justify-center transition-all bg-red-100 text-red-700 hover:bg-red-200"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];
};